import { Venue, Booking, InquiryDraft, PartnerNotification, VenueClaim } from '../types';

const API_BASE = '/api/v1';

export class ApiService {
  private static token: string | null = null;

  public static setAuthToken(token: string | null) {
    this.token = token;
    if (token) {
      localStorage.setItem('mehfil_partner_token', token);
    } else {
      localStorage.removeItem('mehfil_partner_token');
    }
  }

  public static getAuthToken(): string | null {
    if (!this.token) {
      this.token = localStorage.getItem('mehfil_partner_token');
    }
    return this.token;
  }

  private static getHeaders(includeAuth = false, isJson = true): HeadersInit {
    const headers: Record<string, string> = {};
    if (isJson) {
      headers['Content-Type'] = 'application/json';
    }
    if (includeAuth) {
      const token = this.getAuthToken();
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
    }
    return headers;
  }

  // --- Venues ---
  public static async searchVenues(params?: {
    area?: string;
    minGuests?: number;
    venueType?: string;
    limit?: number;
  }): Promise<{ venues: Venue[]; count: number }> {
    const query = new URLSearchParams();
    if (params?.area) query.set('area', params.area);
    if (params?.minGuests) query.set('minGuests', params.minGuests.toString());
    if (params?.venueType) query.set('venueType', params.venueType);
    if (params?.limit) query.set('limit', params.limit.toString());

    const res = await fetch(`${API_BASE}/venues?${query.toString()}`);
    if (!res.ok) throw new Error('Failed to fetch venues');
    return res.json();
  }

  public static async getVenue(idOrSlug: string): Promise<{ venue: Venue; packages: any[]; images: any[] }> {
    const res = await fetch(`${API_BASE}/venues/${encodeURIComponent(idOrSlug)}`);
    if (!res.ok) throw new Error('Venue not found');
    return res.json();
  }

  public static async getVenueAvailability(
    venueId: string,
    startDate?: string,
    endDate?: string
  ): Promise<{ availability: any[] }> {
    const query = new URLSearchParams();
    if (startDate) query.set('startDate', startDate);
    if (endDate) query.set('endDate', endDate);

    const res = await fetch(`${API_BASE}/venues/${encodeURIComponent(venueId)}/availability?${query.toString()}`);
    if (!res.ok) throw new Error('Failed to fetch venue availability');
    return res.json();
  }

  // --- Sessions ---
  public static async initSession(deviceId: string, existingSessionId?: string): Promise<{ session: any; deviceId: string }> {
    const res = await fetch(`${API_BASE}/sessions/init`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify({ deviceId, existingSessionId })
    });
    if (!res.ok) throw new Error('Failed to initialize session');
    return res.json();
  }

  public static async sendMagicLink(sessionId: string, email: string): Promise<{ success: boolean; message: string; magicUrl: string }> {
    const res = await fetch(`${API_BASE}/sessions/magic-link`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify({ sessionId, email })
    });
    if (!res.ok) throw new Error('Failed to send magic link');
    return res.json();
  }

  public static async resumeSession(token: string, deviceId: string): Promise<{ success: boolean; session: any; deviceId: string }> {
    const res = await fetch(`${API_BASE}/sessions/resume/${encodeURIComponent(token)}?deviceId=${encodeURIComponent(deviceId)}`);
    if (!res.ok) throw new Error('Failed to resume session');
    return res.json();
  }

  // --- Bookings ---
  public static async prepareDraft(data: {
    sessionId?: string;
    venueId: string;
    eventType: string;
    eventDate: string;
    slot: 'lunch' | 'dinner';
    guestCount: number;
    customerName: string;
    customerPhone: string;
    customerEmail?: string;
    packageId?: string;
    customerNotes?: string;
  }): Promise<InquiryDraft> {
    const res = await fetch(`${API_BASE}/bookings/draft`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify(data)
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || 'Failed to prepare booking draft');
    }
    return res.json();
  }

  public static async commitInquiry(draftBookingId: string, confirmedBy = 'tap'): Promise<Booking> {
    const res = await fetch(`${API_BASE}/bookings/commit`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify({ draftBookingId, confirmedBy })
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || 'Failed to commit booking');
    }
    return res.json();
  }

  public static async verifyBooking(code: string): Promise<any> {
    const res = await fetch(`/verify/${encodeURIComponent(code)}`, {
      headers: { Accept: 'application/json' }
    });
    if (!res.ok) throw new Error('Booking verification failed');
    return res.json();
  }

  // --- Partner Portal ---
  public static async partnerRegister(data: { name: string; email: string; phone?: string; password: string }): Promise<{ token: string; user: any }> {
    const res = await fetch(`${API_BASE}/partner/auth/register`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify(data)
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || 'Registration failed');
    }
    const result = await res.json();
    this.setAuthToken(result.token);
    return result;
  }

  public static async partnerLogin(data: { email: string; password: string }): Promise<{ token: string; user: any }> {
    const res = await fetch(`${API_BASE}/partner/auth/login`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify(data)
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || 'Login failed');
    }
    const result = await res.json();
    this.setAuthToken(result.token);
    return result;
  }

  public static async partnerGetMe(): Promise<{ user: any }> {
    const res = await fetch(`${API_BASE}/partner/auth/me`, {
      headers: this.getHeaders(true)
    });
    if (!res.ok) throw new Error('Unauthorized');
    return res.json();
  }

  public static async claimVenue(data: { venueId: string; contactPhone: string; proofNotes?: string }): Promise<{ success: boolean; claim: any }> {
    const res = await fetch(`${API_BASE}/partner/venues/claim`, {
      method: 'POST',
      headers: this.getHeaders(true),
      body: JSON.stringify(data)
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || 'Failed to submit claim');
    }
    return res.json();
  }

  public static async uploadVenuePhoto(venueId: string, file: File, category = 'hall', caption?: string): Promise<{ success: boolean; image: any }> {
    const formData = new FormData();
    formData.append('photo', file);
    formData.append('category', category);
    if (caption) formData.append('caption', caption);

    const token = this.getAuthToken();
    const headers: Record<string, string> = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;

    const res = await fetch(`${API_BASE}/partner/venues/${encodeURIComponent(venueId)}/images`, {
      method: 'POST',
      headers,
      body: formData
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || 'Image upload failed');
    }
    return res.json();
  }

  public static async updateVenuePackages(venueId: string, packages: any[]): Promise<{ success: boolean }> {
    const res = await fetch(`${API_BASE}/partner/venues/${encodeURIComponent(venueId)}/packages`, {
      method: 'PUT',
      headers: this.getHeaders(true),
      body: JSON.stringify({ packages })
    });
    if (!res.ok) throw new Error('Failed to update packages');
    return res.json();
  }

  public static async updateVenueAvailability(venueId: string, slots: { date: string; slot: 'lunch' | 'dinner'; status: string }[]): Promise<{ success: boolean }> {
    const res = await fetch(`${API_BASE}/partner/venues/${encodeURIComponent(venueId)}/availability`, {
      method: 'PUT',
      headers: this.getHeaders(true),
      body: JSON.stringify({ slots })
    });
    if (!res.ok) throw new Error('Failed to update availability');
    return res.json();
  }

  public static async replyToBooking(bookingId: string, message: string, counterQuotePkr?: number | null): Promise<{ success: boolean; inquiryMessage: any }> {
    const res = await fetch(`${API_BASE}/partner/bookings/${encodeURIComponent(bookingId)}/reply`, {
      method: 'POST',
      headers: this.getHeaders(true),
      body: JSON.stringify({ message, counterQuotePkr })
    });
    if (!res.ok) throw new Error('Failed to send reply');
    return res.json();
  }

  public static async getPartnerNotifications(venueId?: string): Promise<{ notifications: PartnerNotification[] }> {
    const query = venueId ? `?venueId=${encodeURIComponent(venueId)}` : '';
    const res = await fetch(`${API_BASE}/partner/notifications${query}`, {
      headers: this.getHeaders(true)
    });
    if (!res.ok) throw new Error('Failed to fetch notifications');
    return res.json();
  }

  // --- Admin Console ---
  public static async adminSweepEnrichment(corridor: string, lat?: number, lng?: number): Promise<any> {
    const res = await fetch(`${API_BASE}/admin/enrichment/sweep`, {
      method: 'POST',
      headers: this.getHeaders(true),
      body: JSON.stringify({ corridor, lat, lng })
    });
    if (!res.ok) throw new Error('Failed to run enrichment sweep');
    return res.json();
  }

  public static async adminGetClaims(): Promise<{ claims: VenueClaim[] }> {
    const res = await fetch(`${API_BASE}/admin/claims`, {
      headers: this.getHeaders(true)
    });
    if (!res.ok) throw new Error('Failed to fetch claims');
    return res.json();
  }

  public static async adminReviewClaim(claimId: string, status: 'approved' | 'rejected'): Promise<any> {
    const res = await fetch(`${API_BASE}/admin/claims/${encodeURIComponent(claimId)}/review`, {
      method: 'POST',
      headers: this.getHeaders(true),
      body: JSON.stringify({ status })
    });
    if (!res.ok) throw new Error('Failed to review claim');
    return res.json();
  }

  public static async adminGetStats(): Promise<any> {
    const res = await fetch(`${API_BASE}/admin/stats`, {
      headers: this.getHeaders(true)
    });
    if (!res.ok) throw new Error('Failed to get stats');
    return res.json();
  }

  public static async adminTestSmtp(to = 'subhan.z.g@gmail.com'): Promise<any> {
    const res = await fetch(`${API_BASE}/admin/email-test`, {
      method: 'POST',
      headers: this.getHeaders(true),
      body: JSON.stringify({ to })
    });
    if (!res.ok) throw new Error('SMTP test failed');
    return res.json();
  }
}
